/**
 * Readiness Definition Store
 *
 * Zustand store for readiness_definitions.
 * Workspace-scoped, DB-first. No localStorage.
 *
 * Inheritance: session ?? track ?? cycle ?? workspace. The workspace layer is
 * workspaces.readiness_definition_id, loaded here with the definitions and used
 * when a caller does not pass it. Every definition also includes the every-item Hard Stops
 * (lib/itemReadiness.ts evaluateReadyForRanking).
 *
 * Usage:
 *   const { loadForWorkspace, definitions, getEffectiveDefinition } = useReadinessDefinitionStore();
 */

import { create } from 'zustand';
import { useTenantStore } from './tenantStore';
import {
  fetchReadinessDefinitions,
  createReadinessDefinition,
  updateReadinessDefinition,
  deleteReadinessDefinition,
  assignReadinessDefinition,
  fetchWorkspaceReadinessDefinitionId,
  type CreateReadinessDefinitionInput,
  type UpdateReadinessDefinitionInput,
} from '../../lib/readinessDefinitionService';
import {
  resolveEffectiveReadinessDefinition,
} from '../../lib/readinessDefinitionResolver';
import type {
  ReadinessDefinition,
  EffectiveReadinessDefinition,
} from '../../types/prioritization/readinessDefinition';

// ── State ─────────────────────────────────────────────────────────────────────

interface ReadinessDefinitionState {
  workspaceId: string | null;
  definitions: ReadinessDefinition[];
  /** workspaces.readiness_definition_id for workspaceId */
  workspaceDefinitionId: string | null;
  loading: boolean;
  error: string | null;

  /**
   * Load all definitions for the workspace.
   * No-ops if already loaded for the same workspace.
   */
  loadForWorkspace: (workspaceId: string) => Promise<void>;

  /**
   * Resolve the effective definition for a given context.
   *
   * Reads readiness_definition_id from each context object if provided,
   * then resolves via: session ?? track ?? cycle ?? workspace.
   */
  getEffectiveDefinition: (opts: {
    workspaceDefinitionId?: string | null;
    cycleDefinitionId?: string | null;
    trackDefinitionId?: string | null;
    sessionDefinitionId?: string | null;
  }) => EffectiveReadinessDefinition;

  /** The workspace's own definition id (workspaces.readiness_definition_id), or null. */
  getWorkspaceDefinitionId: () => string | null;

  /** Make a definition the workspace's own (or clear it with null). */
  setWorkspaceDefinition: (definitionId: string | null) => Promise<boolean>;

  /** Create a new readiness definition */
  create: (input: CreateReadinessDefinitionInput) => Promise<ReadinessDefinition | null>;

  /** Update an existing definition (optimistic) */
  update: (input: UpdateReadinessDefinitionInput) => Promise<ReadinessDefinition | null>;

  /** Delete a definition */
  delete: (id: string) => Promise<boolean>;

  /** Clear all state (workspace switch / logout) */
  clear: () => void;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useReadinessDefinitionStore = create<ReadinessDefinitionState>()((set, get) => ({
  workspaceId: null,
  definitions: [],
  workspaceDefinitionId: null,
  loading: false,
  error: null,

  loadForWorkspace: async (workspaceId) => {
    const state = get();
    if (state.workspaceId === workspaceId && state.definitions.length > 0) return;
    if (state.loading) return;

    set({ loading: true, error: null, workspaceId });
    const [data, workspaceDefinitionId] = await Promise.all([
      fetchReadinessDefinitions(workspaceId),
      fetchWorkspaceReadinessDefinitionId(workspaceId),
    ]);
    set({ loading: false, definitions: data, workspaceDefinitionId });
  },

  getWorkspaceDefinitionId: () => get().workspaceDefinitionId,

  setWorkspaceDefinition: async (definitionId) => {
    const tenant = useTenantStore.getState();
    const workspaceId = get().workspaceId ?? tenant.currentWorkspaceId;
    if (!workspaceId) return false;
    const ok = await assignReadinessDefinition('workspace', workspaceId, definitionId);
    if (ok) {
      set({ workspaceDefinitionId: definitionId });
      useTenantStore.setState({
        workspaces: tenant.workspaces.map((w) =>
          w.id === workspaceId ? { ...w, readinessDefinitionId: definitionId } : w
        ),
      });
    }
    return ok;
  },

  getEffectiveDefinition: ({ workspaceDefinitionId, cycleDefinitionId, trackDefinitionId, sessionDefinitionId }) => {
    const { definitions } = get();
    return resolveEffectiveReadinessDefinition(
      definitions,
      workspaceDefinitionId === undefined ? get().getWorkspaceDefinitionId() : workspaceDefinitionId,
      cycleDefinitionId,
      trackDefinitionId,
      sessionDefinitionId
    );
  },

  create: async (input) => {
    const result = await createReadinessDefinition(input);
    if (result) {
      set((s) => ({ definitions: [...s.definitions, result] }));
    }
    return result;
  },

  update: async (input) => {
    // Optimistic update
    set((s) => ({
      definitions: s.definitions.map((d) =>
        d.id === input.id
          ? {
              ...d,
              ...(input.name !== undefined && { name: input.name }),
              ...(input.description !== undefined && { description: input.description }),
              ...(input.criteria !== undefined && { criteria: input.criteria }),
            }
          : d
      ),
    }));

    const result = await updateReadinessDefinition(input);
    if (result) {
      set((s) => ({
        definitions: s.definitions.map((d) => (d.id === result.id ? result : d)),
      }));
    }
    return result;
  },

  delete: async (id) => {
    const ok = await deleteReadinessDefinition(id);
    if (ok) {
      set((s) => ({
        definitions: s.definitions.filter((d) => d.id !== id),
        workspaceDefinitionId: s.workspaceDefinitionId === id ? null : s.workspaceDefinitionId,
      }));
      // The FK is ON DELETE SET NULL; mirror it so the page does not point at a gone row.
      const tenant = useTenantStore.getState();
      if (tenant.workspaces.some((w) => w.readinessDefinitionId === id)) {
        useTenantStore.setState({
          workspaces: tenant.workspaces.map((w) => (w.readinessDefinitionId === id ? { ...w, readinessDefinitionId: null } : w)),
        });
      }
    }
    return ok;
  },

  clear: () => set({ workspaceId: null, definitions: [], workspaceDefinitionId: null, loading: false, error: null }),
}));
